/**
 * BlockSchemaForm
 *
 * Renders a dynamic form from a FieldSchema[] definition.
 * Used by BlockConfigPanel and any inline field-editing surface.
 */
import React, { useRef, useState } from 'react';
import type { FieldSchema } from './types';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { Plus, Trash2, ChevronUp, ChevronDown, Upload, X, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import IconPicker from './IconPicker';
import EnhancedColorPicker from './EnhancedColorPicker';

/* ------------------------------------------------------------------ */
/*  Image sub-field                                                    */
/* ------------------------------------------------------------------ */
function ImageField({
  value,
  onChange,
}: {
  schema?: FieldSchema;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const url = typeof value === 'string' ? value : '';
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('images', file);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await api.post('/admin/page-templates/images/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data: unknown = res?.data ?? res;
      const arr = Array.isArray(data)
        ? data
        : Array.isArray((data as Record<string, unknown>)?.data)
        ? (data as Record<string, unknown[]>).data
        : null;
      const first = Array.isArray(arr) ? arr[0] : null;
      if (first && typeof (first as Record<string, unknown>).url === 'string') {
        onChange((first as Record<string, unknown>).url);
      }
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {url && (
        <div className="relative w-full h-28 rounded-lg overflow-hidden border bg-muted">
          <img src={url} alt="" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="shrink-0"
        >
          {uploading ? (
            <Loader2 className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <Upload className="w-3 h-3 mr-1" />
          )}
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
        <Input
          value={url}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or paste image URL"
          className="text-xs"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  List sub-field                                                     */
/* ------------------------------------------------------------------ */
function ListField({
  schema,
  value,
  onChange,
}: {
  schema: FieldSchema;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const listSchema = schema.listSchema ?? [];
  const items: Record<string, unknown>[] = Array.isArray(value)
    ? (value as Record<string, unknown>[])
    : [];

  const addItem = () => {
    const empty: Record<string, unknown> = {};
    listSchema.forEach((f) => { empty[f.key] = f.defaultValue ?? ''; });
    onChange([...items, empty]);
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const moveItem = (idx: number, dir: 'up' | 'down') => {
    const target = dir === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const updateItem = (idx: number, key: string, val: unknown) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="p-3 rounded-lg border border-border bg-background space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-muted-foreground">#{idx + 1}</span>
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => moveItem(idx, 'up')}
                disabled={idx === 0}
                className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-20"
              >
                <ChevronUp size={12} />
              </button>
              <button
                onClick={() => moveItem(idx, 'down')}
                disabled={idx === items.length - 1}
                className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-20"
              >
                <ChevronDown size={12} />
              </button>
              <button
                onClick={() => removeItem(idx)}
                className="p-1 text-red-500 hover:text-red-700"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>
          {listSchema.map((subField) => (
            <SingleField
              key={subField.key}
              schema={subField}
              value={item[subField.key]}
              onChange={(v) => updateItem(idx, subField.key, v)}
            />
          ))}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addItem} className="w-full">
        <Plus size={13} className="mr-1" />
        Add {schema.label?.replace(/s$/, '') || 'Item'}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Single field renderer                                              */
/* ------------------------------------------------------------------ */
function SingleField({
  schema,
  value,
  onChange,
}: {
  schema: FieldSchema;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const strVal = value !== undefined && value !== null ? String(value) : (schema.defaultValue !== undefined ? String(schema.defaultValue) : '');

  switch (schema.type) {
    case 'image':
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <ImageField schema={schema} value={value} onChange={onChange} />
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );

    case 'textarea':
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <Textarea
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
            placeholder={schema.placeholder ?? schema.label}
            rows={3}
          />
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );

    case 'richtext':
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <Textarea
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
            placeholder={schema.placeholder ?? schema.label}
            rows={6}
          />
          <p className="text-[11px] text-muted-foreground/60">Supports Markdown</p>
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );

    case 'number':
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <Input
            type="number"
            value={strVal}
            onChange={(e) => onChange(Number(e.target.value))}
            min={schema.min}
            max={schema.max}
            placeholder={schema.placeholder}
          />
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );

    case 'boolean':
      return (
        <div className="flex items-center justify-between py-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <Switch
            checked={Boolean(value ?? schema.defaultValue ?? false)}
            onCheckedChange={onChange}
          />
        </div>
      );

    case 'select':
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <Select
            value={strVal}
            onValueChange={onChange}
          >
            <SelectTrigger>
              <SelectValue placeholder={schema.placeholder ?? 'Select…'} />
            </SelectTrigger>
            <SelectContent>
              {(schema.options ?? []).map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );

    case 'color':
      return (
        <div className="space-y-1">
          <EnhancedColorPicker
            value={strVal || '#000000'}
            onChange={(v) => onChange(v)}
            label={schema.label}
          />
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );

    case 'list':
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <ListField schema={schema} value={value} onChange={onChange} />
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );

    case 'json':
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <Textarea
            value={typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2)}
            onChange={(e) => {
              try {
                onChange(JSON.parse(e.target.value));
              } catch {
                /* ignore parse errors while typing */
              }
            }}
            rows={6}
            className="font-mono text-xs"
          />
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );

    case 'icon':
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <IconPicker
            value={String(value ?? '')}
            onChange={(v) => onChange(v)}
          />
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );

    // text | entity | component — all fall through to text input
    default:
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">{schema.label}</Label>
          <Input
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
            placeholder={schema.placeholder ?? schema.label}
          />
          {schema.helpText && (
            <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>
          )}
        </div>
      );
  }
}

/* ------------------------------------------------------------------ */
/*  Public component                                                   */
/* ------------------------------------------------------------------ */

export interface BlockSchemaFormProps {
  schema: FieldSchema[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}

/**
 * Renders a vertical stack of field editors driven by a FieldSchema[].
 */
export default function BlockSchemaForm({
  schema,
  values,
  onChange,
}: BlockSchemaFormProps) {
  if (schema.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        This block has no configurable fields.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {schema.map((field) => (
        <SingleField
          key={field.key}
          schema={field}
          value={values[field.key]}
          onChange={(v) => onChange(field.key, v)}
        />
      ))}
    </div>
  );
}
